<script setup lang="ts">
  import { IconEdit } from '@gat-ship/icons'
  import type { AgencyGroup } from '@gat-ship/types'
  // fallow-ignore-next-line code-duplication
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { inject } from 'vue'
  import { agencyStructureKey } from '@gat-ship/types/injection-keys'

  const isOpen = defineModel<boolean>('open', { default: false })
  const agencyStructureProvider = inject(agencyStructureKey)
  const description = ref('')

  const changeAgencyGroupNameSchema = z.object({
    agencyGroupId: z.string().min(1),
    name: z.string().min(1, 'Name is required').max(120, 'Max 120 characters'),
    shortName: z
      .string()
      .min(1, 'Short Name is required')
      .max(120, 'Max 120 characters')
  })
  type ChangeAgencyGroupNameFormValues = z.infer<
    typeof changeAgencyGroupNameSchema
  >

  const form = useForm({
    defaultValues: {
      agencyGroupId: '',
      name: '',
      shortName: ''
    } as ChangeAgencyGroupNameFormValues,
    validators: {
      onChange: changeAgencyGroupNameSchema
    },
    onSubmit: async ({ value }) => {
      await agencyStructureProvider?.commands.changeGroupName(
        value.agencyGroupId,
        {
          name: value.name,
          shortName: value.shortName
        }
      )
      isOpen.value = false
    }
  })

  export interface ShowChangeAgencyGroupNameModalParams {
    agencyGroup: AgencyGroup
  }

  function showModal({ agencyGroup }: ShowChangeAgencyGroupNameModalParams) {
    description.value = agencyGroup.name
    form.reset({
      agencyGroupId: agencyGroup.id,
      name: agencyGroup.name,
      shortName: agencyGroup.shortName
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen" class="min-w-[400px]">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconEdit class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Change Agency Group Name
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="description">
        {{ description }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="name" label="Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                :data-testid="testIds.agency.modal.changeName.nameInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField :form="form" name="shortName" label="Short Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="testIds.agency.modal.changeName.shortNameInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Change" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
